import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Form, Input, Row, Space, Switch, Tooltip, Typography } from "antd";
import {
  ApiOutlined,
  CloudOutlined,
  InfoCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { STORAGE_ENDPOINT } from "../constants";

const { Text } = Typography;

/**
 * The platform's S3-compatible connection — one shared bucket for every feature that stores a
 * file, backup archives included.
 *
 * The secret key is stored encrypted and never sent to the browser, so the field starts empty
 * and an empty field means "keep the one on file" — both here and on the server.
 */
const StoragePanel = ({ onChanged }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [form] = Form.useForm();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);

    await mutateRef.current("storageSettings", {
      method: "get",
      endpoint: STORAGE_ENDPOINT,
      onSuccess: (data) => {
        setSettings(data);
        form.setFieldsValue({
          endpoint: data?.endpoint ?? "",
          region: data?.region ?? "",
          bucket: data?.bucket ?? "",
          accessKey: data?.accessKey ?? "",
          secretKey: "",
          prefix: data?.prefix ?? "",
          usePathStyle: data?.usePathStyle ?? true,
          isActive: data?.isActive ?? true,
        });
      },
    });

    setLoading(false);
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  /** Everything the API needs, straight off the form. */
  const payload = (values) => ({
    endpoint: (values.endpoint || "").trim(),
    region: (values.region || "").trim(),
    bucket: (values.bucket || "").trim(),
    accessKey: (values.accessKey || "").trim(),
    secretKey: values.secretKey || "",
    prefix: (values.prefix || "").trim() || null,
    usePathStyle: values.usePathStyle ?? true,
    isActive: values.isActive ?? true,
  });

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);

    await mutate("saveStorageSettings", {
      method: "put",
      endpoint: STORAGE_ENDPOINT,
      payload: payload(values),
      onSuccess: (data) => {
        setSettings(data);
        // The secret was either kept or replaced; either way the box goes back to "unchanged".
        form.setFieldValue("secretKey", "");
        onChanged?.();
      },
    });

    setSaving(false);
  };

  const test = async () => {
    const values = await form.validateFields();
    setTesting(true);
    setTestResult(null);

    await mutate("testStorageSettings", {
      method: "post",
      endpoint: `${STORAGE_ENDPOINT}/test`,
      // The stored secret stays on the server: an empty field lets it fill the blank in.
      payload: payload(values),
      onSuccess: (data) => {
        setTestResult(data);
        // The test stamps LastTestAt on the connection, so re-read it for the banner.
        load();
      },
    });

    setTesting(false);
  };

  const lastTest = settings?.lastTestAt ? new Date(settings.lastTestAt).toLocaleString() : null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="One connection, shared by every feature"
        description="Documents and backup archives go to the same bucket, each under its own folder. The secret key is stored encrypted and never leaves the server."
      />

      <Card
        title={
          <Space size={8}>
            <CloudOutlined />
            S3 connection
          </Space>
        }
        loading={loading}
      >
        {lastTest && (
          <Alert
            style={{ marginBottom: 16 }}
            type={settings.lastTestOk ? "success" : "error"}
            showIcon
            message={
              settings.lastTestOk
                ? `Last test succeeded: ${lastTest} · bucket: ${settings.bucket}`
                : `Last test failed: ${lastTest}`
            }
            description={settings.lastTestOk ? null : settings.lastTestError}
          />
        )}

        <Form form={form} layout="vertical" initialValues={{ usePathStyle: true, isActive: true }}>
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="endpoint"
                label="Endpoint"
                tooltip="Address of the S3-compatible service. https:// is assumed."
                rules={[{ required: true, message: "An endpoint is required." }]}
              >
                <Input placeholder="s3.eu-central-2.wasabisys.com" autoComplete="off" maxLength={300} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="region"
                label="Region"
                tooltip="Region of the bucket, e.g. eu-central-2. It has to match, or the request signature is rejected."
                rules={[{ required: true, message: "A region is required." }]}
              >
                <Input placeholder="eu-central-2" autoComplete="off" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="bucket"
                label="Bucket"
                rules={[{ required: true, message: "A bucket is required." }]}
              >
                <Input placeholder="ticketing" autoComplete="off" maxLength={200} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="prefix"
                label="Base folder"
                tooltip="Optional folder every file is written under, so this bucket can be shared with something else. Archives land in its 'backups' subfolder."
              >
                <Input placeholder="kubitai" autoComplete="off" maxLength={200} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="accessKey"
                label="Access key"
                rules={[{ required: true, message: "An access key is required." }]}
              >
                <Input autoComplete="off" maxLength={200} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="secretKey"
                label="Secret key"
                tooltip={
                  settings?.hasSecretKey
                    ? "Leave empty to keep the stored key."
                    : "Stored encrypted; it is never sent back to this screen."
                }
                rules={
                  settings?.hasSecretKey
                    ? []
                    : [{ required: true, message: "A secret key is required." }]
                }
              >
                <Input.Password
                  autoComplete="new-password"
                  placeholder={settings?.hasSecretKey ? "unchanged — leave empty to keep it" : ""}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="usePathStyle"
                label={
                  <Space size={6}>
                    Path-style addressing
                    <Tooltip title="Addresses the bucket as endpoint/bucket/key. Recommended for Wasabi, MinIO and most S3-compatible services.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="isActive"
                label="Active"
                valuePropName="checked"
                tooltip="When off, no feature can upload — the connection stays configured but idle."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Space size={12} wrap>
            <Button variant="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
              Save connection
            </Button>

            <Button variant="secondary" icon={<ApiOutlined />} loading={testing} onClick={test}>
              Test connection
            </Button>
          </Space>

          {testResult && (
            <Alert
              style={{ marginTop: 16 }}
              type={testResult.connected && testResult.canWrite ? "success" : "error"}
              showIcon
              message={
                testResult.connected
                  ? testResult.canWrite
                    ? `Connected and wrote a test file in ${testResult.elapsedMs} ms`
                    : "Connected, but the bucket is not writable"
                  : "Could not connect"
              }
              description={
                testResult.connected ? (
                  <Space direction="vertical" size={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Uploads land in <Text code>{testResult.bucket}</Text>
                      {testResult.prefix ? (
                        <>
                          {" "}
                          under <Text code>{testResult.prefix}/</Text>
                        </>
                      ) : null}
                    </Text>
                    {testResult.error && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {testResult.error}
                      </Text>
                    )}
                  </Space>
                ) : (
                  testResult.error
                )
              }
            />
          )}
        </Form>
      </Card>
    </Space>
  );
};

export default StoragePanel;
